import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { KeyRound, User, Eye, EyeOff } from "lucide-react"; // 🛠️ ADDED Eye, EyeOff

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { post } from "@/lib/api-client";
import { rotateCorrelationId } from "@/lib/correlation";
import { useAuthStore } from "@/store/auth-store";
import { toast } from "sonner";
import type { AuthTokens, LoginPayload, User as UserT } from "@/types/auth";

export function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // 🛠️ ADDED State for show/hide

  const { register, handleSubmit, formState: { errors } } = useForm<LoginPayload>();
  const setSession = useAuthStore((s) => s.setSession);
  const nav = useNavigate();

  const onSubmit = async (data: LoginPayload) => {
    setLoading(true);
    rotateCorrelationId();
    try {
      const r = await post<{ access: string; refresh: string; user: UserT }>(
        "/auth/login", data,
      );
      const tokens: AuthTokens = { access: r.access, refresh: r.refresh };
      setSession(r.user, tokens);
      toast.success(`Welcome back, ${r.user.full_name}`);
      nav("/", { replace: true });
    } catch { /* interceptor toasts */ }
    finally { setLoading(false); }
  };

  return (
    <div className="animate-fade-in">
      <h1 className="text-2xl font-bold text-surface-900">Sign in</h1>
      <p className="text-sm text-surface-500 mt-1.5">
        Enter your credentials to access the customer portal.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-4">
        <Input
          label="User ID or Mobile" // 🛠️ Updated label to show users can use Mobile too
          placeholder="MS000001 or +8801..."
          leftIcon={<User className="h-4 w-4" />}
          autoComplete="username"
          error={errors.user_id?.message}
          {...register("user_id", { required: "User ID or Mobile is required" })}
        />
        
        {/* 🛠️ ADDED: Show/Hide Toggle for Password */}
        <Input
          label="Password"
          type={showPassword ? "text" : "password"}
          placeholder="••••••••••"
          leftIcon={<KeyRound className="h-4 w-4" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-surface-400 hover:text-surface-600 focus:outline-none"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          }
          autoComplete="current-password"
          error={errors.password?.message}
          {...register("password", { required: "Password is required" })}
        />

        <div className="flex items-center justify-end -mt-1">
          <Link to="/register" className="text-sm text-brand-600 hover:text-brand-700 font-medium">
             Register Now
          </Link>
        </div>
        <Button type="submit" fullWidth size="lg" loading={loading}>Sign in</Button>
      </form>

      <div className="mt-8 p-3.5 rounded-lg bg-surface-100 text-xs text-surface-600">
        <div className="font-semibold text-surface-800 mb-1">Security</div>
        This portal enforces strong-password policy, lockout after 5 failed attempts,
        and single-device or multi-device sessions per your account policy.
      </div>
    </div>
  );
}